import { useCallback } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
  SettingsType,
  getActivePipeexProfile,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import type { ConnectorDocumentIdentity } from '../../../shared/lib/connectorTarget';
import { buildProfileKey, parseSettingsType } from '../lib/profile';
import { warnMappingFlow } from '../lib/mappingTrace';
import {
  runPcfProfileCheckpoint,
  type PcfProfileCheckpointTarget,
} from '../lib/pcfProfileCheckpoint';
import {
  currentSetupRevisionId,
  recordVerifiedSetupBaseline,
} from './profileControllerShared';

interface VerifiedProfileSelection {
  name: string;
  settingsType: SettingsType;
  profileKey: string;
}

interface UseVerifiedMappingProfileSaveArgs {
  activeUserId: string | null;
  activeProjectKey: string | null;
  saveConfiguration: (profileName?: string, settingsType?: SettingsType) => Promise<void> | void;
  setupIdentity: ConnectorDocumentIdentity | null;
  selectedProfileKeyRef: MutableRefObject<string>;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  setSaveError: Dispatch<SetStateAction<string | null>>;
  markWorkflowApplied: () => void;
  markProjectSetupCompleted: (
    userId: string | null | undefined,
    projectKey: string | null | undefined,
  ) => void;
  onProfileConfirmed?: (profile: VerifiedProfileSelection) => void;
  onContinue: () => void;
}

const parseProfileKeyTarget = (profileKey: string): PcfProfileCheckpointTarget | null => {
  if (!profileKey) {
    return null;
  }
  const [name, settingsTypeRaw] = profileKey.split('::');
  return name ? { name, settingsType: parseSettingsType(settingsTypeRaw) } : null;
};

const resolveReadBackTarget = (
  selected: PcfProfileCheckpointTarget | null,
): PcfProfileCheckpointTarget | null => {
  if (selected) {
    return selected;
  }
  const active = getActivePipeexProfile();
  return active?.name ? { name: active.name, settingsType: active.settingsType } : null;
};

export const useVerifiedMappingProfileSave = ({
  activeUserId,
  activeProjectKey,
  saveConfiguration,
  setupIdentity,
  selectedProfileKeyRef,
  setIsSaving,
  setSaveError,
  markWorkflowApplied,
  markProjectSetupCompleted,
  onProfileConfirmed,
  onContinue,
}: UseVerifiedMappingProfileSaveArgs) => {
  const persistConfiguration = useCallback(async (closeAfterSave: boolean): Promise<boolean> => {
    setIsSaving(true);
    setSaveError(null);

    try {
      const currentProfileKey = selectedProfileKeyRef.current;
      const selectedTarget = parseProfileKeyTarget(currentProfileKey);
      const readBackTarget = resolveReadBackTarget(selectedTarget);
      const revisionId = currentSetupRevisionId();
      const checkpoint = await runPcfProfileCheckpoint({
        revisionId,
        currentRevisionId: currentSetupRevisionId,
        identity: setupIdentity,
        target: readBackTarget,
        save: async () => {
          if (selectedTarget) {
            await saveConfiguration(selectedTarget.name, selectedTarget.settingsType);
          } else {
            await saveConfiguration();
          }
        },
      });

      if (checkpoint.kind === 'superseded') {
        return false;
      }
      if (checkpoint.kind === 'mismatch') {
        warnMappingFlow('profile read-back rejected', {
          profileKey: currentProfileKey,
          revisionId,
          reason: checkpoint.reason,
        });
        setSaveError(checkpoint.message);
        return false;
      }

      recordVerifiedSetupBaseline({
        profileKey: readBackTarget
          ? buildProfileKey(readBackTarget.name, readBackTarget.settingsType)
          : '',
        setupFingerprint: checkpoint.setupFingerprint,
        revisionId,
        identity: setupIdentity,
      });

      if (selectedTarget) {
        onProfileConfirmed?.({
          name: selectedTarget.name,
          settingsType: selectedTarget.settingsType,
          profileKey: buildProfileKey(selectedTarget.name, selectedTarget.settingsType),
        });
      }
      markWorkflowApplied();
      markProjectSetupCompleted(activeUserId, activeProjectKey);
      if (closeAfterSave) {
        onContinue();
      }
      return true;
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSaveError(error instanceof Error ? error.message : 'An unknown error occurred while saving');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    activeProjectKey,
    activeUserId,
    markProjectSetupCompleted,
    markWorkflowApplied,
    onContinue,
    onProfileConfirmed,
    saveConfiguration,
    selectedProfileKeyRef,
    setIsSaving,
    setSaveError,
    setupIdentity,
  ]);

  const handleApply = useCallback(() => persistConfiguration(false), [persistConfiguration]);
  const handleContinue = useCallback(() => persistConfiguration(true), [persistConfiguration]);

  return { handleApply, handleContinue };
};
